import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { StatusBadge } from './RequisitionsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_WD = {
  amount: '', date: format(new Date(), 'yyyy-MM-dd'), time: '', bank_account: '',
  purpose: '', program: '', recipient: '', expected_use: '', requisition_id: '', notes: '',
};

export const WithdrawalsTab = ({ onChanged }) => {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [approvedReqs, setApprovedReqs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_WD);
  const [detail, setDetail] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reconOpen, setReconOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [selectedExp, setSelectedExp] = useState([]);
  const [actualUse, setActualUse] = useState('');

  const canApprove = hasPermission('requisitions', 'approve');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/finance/withdrawals`);
      setItems(r.data.items || []);
    } catch {
      toast.error('Failed to load withdrawals');
    }
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/finance/requisitions`, { params: { status: 'approved' } })
      .then((r) => setApprovedReqs(r.data.items || [])).catch(() => {});
  }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/finance/withdrawals`, {
        ...form,
        amount: parseFloat(form.amount),
        time: form.time || null,
        program: form.program || null,
        expected_use: form.expected_use || null,
        requisition_id: form.requisition_id || null,
        notes: form.notes || null,
      });
      toast.success('Withdrawal request submitted');
      setCreateOpen(false);
      setForm(EMPTY_WD);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create withdrawal');
    }
  };

  const action = async (id, path, body = {}) => {
    try {
      const r = await axios.post(`${API_URL}/api/finance/withdrawals/${id}/${path}`, body);
      toast.success(`Withdrawal ${path}d`);
      setDetail(r.data);
      setRejectReason('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${path}`);
    }
  };

  const openReconcile = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/expenses`);
      setExpenses((r.data || []).slice(0, 100));
      setSelectedExp(detail.linked_expense_ids || []);
      setActualUse(detail.actual_use || '');
      setReconOpen(true);
    } catch {
      toast.error('Failed to load expenses');
    }
  };

  const submitReconcile = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/finance/withdrawals/${detail.id}/reconcile`, {
        expense_ids: selectedExp, actual_use: actualUse || null,
      });
      toast.success('Withdrawal reconciled');
      setDetail(r.data);
      setReconOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
    }
  };

  const selectedTotal = expenses.filter((e) => selectedExp.includes(e.id)).reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)} data-testid="new-withdrawal-btn">
          <Plus className="mr-2 h-4 w-4" /> New Cash Withdrawal
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reconciliation</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No cash withdrawals yet</TableCell></TableRow>
              ) : items.map((w) => (
                <TableRow key={w.id} className="table-row-hover">
                  <TableCell className="font-mono text-xs">{w.wd_number}</TableCell>
                  <TableCell>{w.date}</TableCell>
                  <TableCell>{w.recipient}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{w.purpose}</TableCell>
                  <TableCell><StatusBadge status={w.status} /></TableCell>
                  <TableCell><StatusBadge status={w.reconciliation_status} /></TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(w.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setDetail(w)} data-testid={`view-wd-${w.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Cash / Money Withdrawal</DialogTitle>
            <DialogDescription>Track a withdrawal from request through reconciliation</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount (UGX) *</Label>
                <Input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="wd-amount" /></div>
              <div className="space-y-1.5"><Label>Date *</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="wd-date" /></div>
              <div className="space-y-1.5"><Label>Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="wd-time" /></div>
              <div className="space-y-1.5"><Label>Bank / Account *</Label>
                <Input required value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="wd-bank" /></div>
              <div className="space-y-1.5"><Label>Person Receiving *</Label>
                <Input required value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} data-testid="wd-recipient" /></div>
              <div className="space-y-1.5"><Label>Program</Label>
                <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                  <SelectTrigger data-testid="wd-program"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">School Program</SelectItem>
                    <SelectItem value="women">Women Empowerment</SelectItem>
                    <SelectItem value="general">General / Org</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Linked Requisition</Label>
              <Select value={form.requisition_id} onValueChange={(v) => setForm({ ...form, requisition_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="wd-requisition"><SelectValue placeholder="Optional — approved requisitions" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {approvedReqs.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.req_number} — {formatCurrency(r.amount)}</SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Purpose *</Label>
              <Textarea rows={2} required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="wd-purpose" /></div>
            <div className="space-y-1.5"><Label>Expected Use</Label>
              <Input value={form.expected_use} onChange={(e) => setForm({ ...form, expected_use: e.target.value })} data-testid="wd-expected-use" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="wd-submit-btn">Submit Withdrawal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setReconOpen(false); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="wd-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {detail.wd_number} <StatusBadge status={detail.status} /> <StatusBadge status={detail.reconciliation_status} />
                </DialogTitle>
                <DialogDescription>{detail.purpose}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Amount</span><p className="font-bold">{formatCurrency(detail.amount)}</p></div>
                <div><span className="text-muted-foreground text-xs">Bank / Account</span><p>{detail.bank_account}</p></div>
                <div><span className="text-muted-foreground text-xs">Recipient</span><p>{detail.recipient}</p></div>
                <div><span className="text-muted-foreground text-xs">Requested by</span><p>{detail.requester_name}</p></div>
                <div><span className="text-muted-foreground text-xs">Spent (reconciled)</span><p className="text-red-600 font-medium">{formatCurrency(detail.spent_total || 0)}</p></div>
                <div><span className="text-muted-foreground text-xs">Remaining balance</span><p className="font-medium">{formatCurrency(detail.remaining_balance || 0)}</p></div>
                {detail.expected_use && <div className="col-span-2"><span className="text-muted-foreground text-xs">Expected use</span><p>{detail.expected_use}</p></div>}
                {detail.actual_use && <div className="col-span-2"><span className="text-muted-foreground text-xs">Actual use</span><p>{detail.actual_use}</p></div>}
                {detail.rejected_reason && <div className="col-span-2 text-red-600"><span className="text-xs">Rejection reason</span><p>{detail.rejected_reason}</p></div>}
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase">Audit Trail</p>
                {(detail.workflow_history || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-l-2 border-primary/40 pl-2 py-0.5">
                    <span className="capitalize font-medium">{h.action.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground">by {h.by_name}</span>
                    <span className="text-muted-foreground">{h.at ? format(new Date(h.at), 'PP p') : ''}</span>
                    {h.note && <span className="italic text-muted-foreground">— {h.note}</span>}
                  </div>
                ))}
              </div>

              {reconOpen && (
                <div className="border rounded-lg p-3 space-y-2" data-testid="wd-reconcile-form">
                  <p className="text-sm font-medium">Reconcile against actual expenses</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {expenses.map((e) => (
                      <label key={e.id} className="flex items-center gap-2 text-xs py-1 border-b last:border-0 cursor-pointer">
                        <Checkbox checked={selectedExp.includes(e.id)}
                          onCheckedChange={(c) => setSelectedExp((prev) => c ? [...prev, e.id] : prev.filter((i) => i !== e.id))}
                          data-testid={`wd-recon-exp-${e.id}`} />
                        <span className="flex-1 truncate">{e.date} · {(e.subcategory || e.category || '').replace(/_/g, ' ')} · {e.description || '—'}</span>
                        <span className="font-medium">{formatCurrency(e.amount)}</span>
                      </label>
                    ))}
                  </div>
                  <Input placeholder="Actual use of funds" value={actualUse} onChange={(e) => setActualUse(e.target.value)} data-testid="wd-actual-use" />
                  <div className="flex items-center justify-between text-sm">
                    <span>Selected: <strong>{formatCurrency(selectedTotal)}</strong> / {formatCurrency(detail.amount)}</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setReconOpen(false)}>Cancel</Button>
                      <Button size="sm" disabled={selectedExp.length === 0} onClick={submitReconcile} data-testid="wd-reconcile-confirm">Reconcile</Button>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2">
                {detail.status === 'submitted' && canApprove && detail.requested_by !== user?.id && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => action(detail.id, 'approve')} data-testid="wd-approve-action">Approve</Button>
                )}
                {detail.status === 'submitted' && canApprove && detail.requested_by !== user?.id && (
                  <div className="flex items-center gap-2">
                    <Input className="h-9 w-44" placeholder="Rejection reason" value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)} data-testid="wd-reject-reason" />
                    <Button size="sm" variant="destructive" disabled={!rejectReason.trim()}
                      onClick={() => action(detail.id, 'reject', { reason: rejectReason })} data-testid="wd-reject-action">Reject</Button>
                  </div>
                )}
                {['approved', 'reconciled'].includes(detail.status) && !reconOpen && (
                  <Button size="sm" onClick={openReconcile} data-testid="wd-reconcile-btn">
                    {detail.status === 'reconciled' ? 'Re-reconcile' : 'Reconcile'}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
